import { useQuery } from '@tanstack/react-query';

import { emptyArrayOf } from 'ui-components/default-props/empty';

import { getInventorySyncConnections } from '../api/inventory-sync-api';
import { inventorySyncQueryKeys } from '../inventory-sync-query-keys';
import { parseLastJobInfo } from '../parse-last-job-info';
import { JOB_STATUS } from '../types/inventory-sync-types';

import type { InventorySyncConnection } from '../types/inventory-sync-types';

/**
 * Floored at 15 seconds: manual push jobs are deliberately invisible to the worker for 60 seconds, so
 * anything faster is pure waste.
 */
const POLL_INTERVAL_MS = 15_000;

/**
 * `new` counts: a job sits in `new` between the row being written and the SQS message being picked up,
 * and treating that as idle means polling never starts, the timestamps go stale, and the manual-sync
 * button stays enabled inviting a duplicate trigger.
 */
const hasInFlightJob = (connections: readonly InventorySyncConnection[]): boolean =>
  connections.some(({ lastJobInfo }) => {
    const status = parseLastJobInfo(lastJobInfo)?.status;
    return status === JOB_STATUS.new || status === JOB_STATUS.enqueued || status === JOB_STATUS.running;
  });

export const useInventorySyncConnections = () => {
  const {
    data: connections = emptyArrayOf<InventorySyncConnection>(),
    isLoading,
    error,
    refetch,
  } = useQuery({
    queryKey: inventorySyncQueryKeys.connections(),
    queryFn: getInventorySyncConnections,
    refetchInterval: ({ state }) => (state.data && hasInFlightJob(state.data) ? POLL_INTERVAL_MS : false),
    refetchIntervalInBackground: false,
  });

  return { connections, isLoading, error, refetch, isSyncInFlight: hasInFlightJob(connections) };
};
